import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackChangeStatus } from 'models/changes'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTrackChangesQuery } from '../../../api/useTrackChangesQuery'
import { useSuggestionDataHandler } from '../../../hooks/useSuggestionDataHandler'
import { useTrackChangesUpdateShallowSelector } from '../store/TrackChangesUpdateStore'
import { TrackChangesUpdateService } from '../TrackChangesUpdateService'
import { FetchFunctionRef, TrackChangesDataMap } from '../types'

/**
 * Hook for managing the fetch function reference
 * Handles keeping the fetch function reference up to date
 */
const useFetchFunction = (): FetchFunctionRef => {
    const fetchFunctionRef = useRef<() => Promise<void>>(() =>
        Promise.resolve()
    )
    const { fetchTrackChangesData } = useTrackChangesQuery()

    // Keep the fetch function reference updated
    useEffect(() => {
        fetchFunctionRef.current = fetchTrackChangesData
    }, [fetchTrackChangesData])

    return fetchFunctionRef
}

/**
 * Enhanced hook for managing the TrackChangesUpdateService instance
 * Handles creation, registration in store, and cleanup with proper typing
 */
const useServiceInstance = (
    fetchFunctionRef: FetchFunctionRef
): TrackChangesUpdateService => {
    const { setService } = useTrackChangesUpdateShallowSelector((state) => ({
        setService: state.setService,
    }))

    const {
        trackChangesData,
        setIsLoadingCards,
    } = useSuggestionsShallowSelector((state) => ({
        trackChangesData: state.trackChangesData,
        setIsLoadingCards: state.setIsLoadingCards,
    }))

    // Create service instance - recreate when data changes to ensure service has current data
    const serviceInstance = useMemo(
        () =>
            new TrackChangesUpdateService(
                {
                    trackChangesData,
                    setIsLoadingCards,
                },
                fetchFunctionRef
            ),
        [trackChangesData, setIsLoadingCards, fetchFunctionRef]
    )

    // Register service in store with proper cleanup
    useEffect(() => {
        setService(serviceInstance)

        return () => {
            // Service cleanup is handled automatically in the store
            setService(null)
        }
    }, [serviceInstance, setService])

    return serviceInstance
}

/**
 * Hook for processing unseen track changes
 * Handles the logic for detecting and processing new/updated track changes with error handling
 */
const useUnseenChangesProcessor = (
    serviceInstance: TrackChangesUpdateService
) => {
    const {
        isTrackedChangesFetched,
        setIsTrackedChangesFetched,
    } = useTrackChangesUpdateShallowSelector((state) => ({
        isTrackedChangesFetched: state.isTrackedChangesFetched,
        setIsTrackedChangesFetched: state.setIsTrackedChangesFetched,
    }))

    const {
        trackChangesData,
        addOrUpdateSuggestions,
    } = useSuggestionsShallowSelector((state) => ({
        trackChangesData: state.trackChangesData,
        addOrUpdateSuggestions: state.addOrUpdateSuggestions,
    }))

    const { buildSuggestions } = useSuggestionDataHandler()
    const prevTrackChangesDataRef = useRef<TrackChangesDataMap>(
        trackChangesData
    )

    // Process unseen changes with error handling
    const processUnseenChanges = useCallback(() => {
        if (!isTrackedChangesFetched) return

        try {
            const unseenChanges = serviceInstance.getUnseenTrackChangesData(
                prevTrackChangesDataRef
            )

            if (unseenChanges && Object.keys(unseenChanges).length > 0) {
                const suggestions = buildSuggestions(
                    Object.values(unseenChanges),
                    TrackChangeStatus.PENDING
                )
                addOrUpdateSuggestions(suggestions, 'merge')
            }

            // Update the reference to current data
            prevTrackChangesDataRef.current = trackChangesData || {}
        } catch (error) {
            const errorMessage = `Failed to process unseen track changes: ${
                error instanceof Error ? error.message : 'Unknown error'
            }`
            console.error(
                'Error processing unseen track changes:',
                errorMessage
            )

            // Don't throw here as it would break the component
            // Instead, reset the fetch flag and continue
        } finally {
            setIsTrackedChangesFetched?.(false)
        }
    }, [
        isTrackedChangesFetched,
        serviceInstance,
        trackChangesData,
        buildSuggestions,
        addOrUpdateSuggestions,
        setIsTrackedChangesFetched,
    ])

    useEffect(() => {
        processUnseenChanges()
    }, [processUnseenChanges])

    return {
        prevTrackChangesDataRef,
        processUnseenChanges, // Expose for manual triggering if needed
    }
}

/**
 * Main hook that orchestrates the TrackChangesUpdateService
 * This hook combines all core service functionality for better maintainability and performance
 * Consolidates fetch function management, service instance creation, and unseen changes processing
 * @returns { serviceInstance: TrackChangesUpdateService }
 */
export const useTrackChangesUpdateService = () => {
    // Manage fetch function reference
    const fetchFunctionRef = useFetchFunction()

    // Create and register service instance
    const serviceInstance = useServiceInstance(fetchFunctionRef)

    // Process unseen changes when they arrive
    useUnseenChangesProcessor(serviceInstance)

    return { serviceInstance }
}
